import { Component } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AnyoTableComponent } from 'projects/app-core/src/app/models/AnyoTableComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { sanitizeHtml } from 'projects/app-core/src/app/utils/sanitize-html.util';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import {
  ReengageAuditDispatchStatus,
  ReengageAuditFilter,
  ReengageAuditGridRequest,
  ReengageAuditLifecycleState,
  ReengageAuditSearch,
} from '../../models/reengage-audit/reengageAuditGridRequest';
import {
  ReengageAuditFilterOptions,
  ReengageAuditGridRow,
} from '../../models/reengage-audit/reengageAuditGridResponse';
import { ReengageAuditService } from '../../services/reengage-audit.service';

interface CheckBoxOptions {
  label: string;
  value: string;
  checked: boolean;
}

@Component({
  selector: 'app-reengage-audit',
  templateUrl: './reengage-audit.component.html',
})
export class ReengageAuditComponent extends AnyoTableComponent<
  ReengageAuditGridRow,
  ReengageAuditGridRequest
> {
  constructor(
    protected override auth: AuthService,
    protected override toastService: ToastService,
    private reengageAuditService: ReengageAuditService,
    private sanitizer: DomSanitizer,
  ) {
    super(auth, toastService);
  }

  emailSearch = '';

  partnerIdCheckBoxOptions: CheckBoxOptions[] = [];
  capabilityIdCheckBoxOptions: CheckBoxOptions[] = [];
  lifecycleStateCheckBoxOptions: CheckBoxOptions[] = Object.values(
    ReengageAuditLifecycleState,
  ).map((value) => ({ label: value, value, checked: false }));
  dispatchStatusCheckBoxOptions: CheckBoxOptions[] = Object.values(
    ReengageAuditDispatchStatus,
  ).map((value) => ({ label: value, value, checked: false }));

  drawerVisible = false;
  selectedRow: ReengageAuditGridRow | null = null;
  selectedHtmlPreview: SafeHtml | null = null;

  override async getData(): Promise<void> {
    this.pageLoading = true;
    this.reengageAuditService
      .getReengageAuditGrid(this.gridRequest as ReengageAuditGridRequest)
      .subscribe({
        next: (value) => {
          this.data = value.data;
          this.totalCount = value.totalRecords;
          this.updateFilterOptions(value.filterOptions);
          this.pageLoading = false;
        },
        error: (err) => {
          const errorBody = err as { error: IAnyoError };
          this.toastService.showAnyoErrorToast(errorBody.error.description);
          this.pageLoading = false;
        },
      });
  }

  private updateFilterOptions(filterOptions?: ReengageAuditFilterOptions): void {
    this.partnerIdCheckBoxOptions = (filterOptions?.partnerId ?? []).map(
      (value) => ({
        label: value,
        value,
        checked: this.isFilterSelected(ReengageAuditFilter.PARTNER_ID, value),
      }),
    );
    this.capabilityIdCheckBoxOptions = (filterOptions?.capabilityId ?? []).map(
      (value) => ({
        label: value,
        value,
        checked: this.isFilterSelected(ReengageAuditFilter.CAPABILITY_ID, value),
      }),
    );
  }

  private isFilterSelected(field: ReengageAuditFilter, value: string): boolean {
    return !!this.gridRequest.filters?.[field]?.includes(value);
  }

  private ensureFilters(): Record<ReengageAuditFilter, string[] | undefined> {
    if (!this.gridRequest.filters) {
      this.gridRequest.filters = {} as Record<
        ReengageAuditFilter,
        string[] | undefined
      >;
    }
    return this.gridRequest.filters;
  }

  applyPartnerIdFilter(values: string[]): void {
    this.ensureFilters()[ReengageAuditFilter.PARTNER_ID] = values;
    this.gridRequest.page = 1;
    this.getData();
  }

  applyCapabilityIdFilter(values: string[]): void {
    this.ensureFilters()[ReengageAuditFilter.CAPABILITY_ID] = values;
    this.gridRequest.page = 1;
    this.getData();
  }

  applyLifecycleStateFilter(values: string[]): void {
    this.ensureFilters()[ReengageAuditFilter.LIFECYCLE_STATE] = values;
    this.gridRequest.page = 1;
    this.getData();
  }

  applyDispatchStatusFilter(values: string[]): void {
    this.ensureFilters()[ReengageAuditFilter.DISPATCH_STATUS] = values;
    this.gridRequest.page = 1;
    this.getData();
  }

  searchByEmail(): void {
    this.gridRequest.searchQuery = {
      [ReengageAuditSearch.EMAIL]: this.emailSearch.trim() || undefined,
    };
    this.gridRequest.page = 1;
    this.getData();
  }

  override async resetFilter(): Promise<void> {
    this.emailSearch = '';
    this.gridRequest = {
      page: 1,
      pageSize: this.pageSize,
      export: false,
    } as ReengageAuditGridRequest;
    this.lifecycleStateCheckBoxOptions = this.lifecycleStateCheckBoxOptions.map(
      (option) => ({ ...option, checked: false }),
    );
    this.dispatchStatusCheckBoxOptions = this.dispatchStatusCheckBoxOptions.map(
      (option) => ({ ...option, checked: false }),
    );
    await this.getData();
  }

  dispatchStatusColor(status: string): string {
    if (status === ReengageAuditDispatchStatus.DISPATCHED) return 'green';
    if (status === ReengageAuditDispatchStatus.SHADOW_SUPPRESSED) return 'orange';
    if (status === ReengageAuditDispatchStatus.SCHEDULED) return 'blue';
    if (
      status === ReengageAuditDispatchStatus.SEND_FAILED ||
      status === ReengageAuditDispatchStatus.SEND_SKIPPED_PERMISSION
    ) {
      return 'red';
    }
    return 'default';
  }

  lifecycleStateColor(state?: string): string {
    if (state === ReengageAuditLifecycleState.RETURNING) return 'blue';
    if (state === ReengageAuditLifecycleState.RESURRECTING) return 'purple';
    return 'default';
  }

  openDetails(row: ReengageAuditGridRow): void {
    this.selectedRow = row;
    this.selectedHtmlPreview = row.htmlGen?.html
      ? this.sanitizer.bypassSecurityTrustHtml(
          sanitizeHtml(row.htmlGen.html),
        )
      : null;
    this.drawerVisible = true;
  }

  closeDetails(): void {
    this.drawerVisible = false;
    this.selectedRow = null;
    this.selectedHtmlPreview = null;
  }
}
